// Global "situational awareness" state: what the user is currently looking at — which page, which
// mode, and a page-specific structured `view` (visible cards, settings + active layer, …). Lives
// OUTSIDE the chat page so SAM can read it from anywhere (the chat is becoming a dockable widget).
//
// Plain, non-persisted slice (mirrors brandSlice). Pages publish their contribution via
// `useRegisterUIContext`; the always-mounted UIContextBridge reads it and emits to the agent.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SamModule } from '@/components/SAM/samActionTypes';

/** Coarse situational mode of the foreground content. */
export type UIMode = 'chat' | 'workspace' | 'settings' | 'generic';

/** Which viewport layout is active (drives whether the sidebar is a drawer or a static column). */
export type Viewport = 'mobile' | 'desktop' | 'unknown';

/** Browser/device environment, captured once on mount. Sent with every agent interaction. */
export interface DeviceContext {
  timezone?: string;
  latitude?: number;
  longitude?: number;
}

/** The always-visible app shell (sidebar nav + top-bar user), so SAM "sees" the chrome too. */
export interface ChromeContext {
  /** Visible sidebar nav labels (role-aware). */
  nav: string[];
  /** True on mobile/tablet where the sidebar is a drawer behind the hamburger menu button. */
  navCollapsed: boolean;
  /** Signed-in user shown in the top bar. */
  user: { name: string | null; email: string | null };
}

/** One visible item in a list/card view (superset of the existing agent.context_update items). */
export interface UIVisibleItem {
  index: number;   // 1-based, matches renderModuleCards
  id: string;      // cardId(module, rec)
  title: string;   // cardInfo(module, rec).title
  status?: string;
  priority?: string;
}

/** Workspace cards currently shown (the chat workspace, or any future card surface). */
export interface WorkspaceView {
  kind: 'workspace';
  module: SamModule;
  /** The filter that produced this list, so SAM knows the scope. */
  filter?: Record<string, unknown>;
  title?: string;
  items: UIVisibleItem[];
  /** Id of the currently expanded card, if any. */
  openCardId?: string | null;
  /** Tool names the agent may call on this module (drives turn-time tool resolution). */
  availableActions?: string[];
  /** Catalogue card on screen (api-service cards/catalogue.py id), e.g. 'task.list' / 'task.view'. */
  cardDefinitionId?: string | null;
}

/** A settings page showing configurable values (Configurations / Tenant settings). */
export interface SettingsView {
  kind: 'settings';
  /** Which settings area is shown, e.g. 'platform.configurations'. */
  area: string;
  /** Active tab or layer the user is looking at, e.g. 'system' | 'tenant' | 'role' | 'user'. */
  activeLayer?: string;
  /** Catalogue keys currently visible — NOT the raw values (permission-scoped). */
  visibleKeys: string[];
  /** Scope the page is focused on, so SAM can reference it. */
  scope?: { tenantId?: string; roleId?: string; userId?: string };
}

/** The chat conversation thread is the foreground surface (history intentionally not included). */
export interface ChatView {
  kind: 'chat';
}

/** Fallback for any page that contributes nothing beyond its route + label. */
export interface GenericView {
  kind: 'generic';
}

export type UIView = WorkspaceView | SettingsView | ChatView | GenericView;

/** The user the agent should treat as the rename target — the focused user card, or in hands-free
 *  the single big card on screen. `id` is the user_id so SAM can call update_user directly. Never
 *  carries email or other sensitive fields. */
export interface SelectedUser {
  id: string;
  name: string;
  role?: string;
  status?: string;
}

/** Shared meeting list filter — written by the agent workspace and read by the standalone
 *  MeetingsPage so both surfaces always show the same slice of data. */
export interface MeetingFilter {
  status?: string;
  meeting_type?: string;
  search?: string;
  organizer_user_id?: string;
  updated_by_user_id?: string;
}

export interface UIContextState {
  /** react-router pathname, e.g. '/platform/configurations'. */
  path: string;
  /** Human page label, e.g. 'Configurations'. */
  pageLabel: string;
  /** Active viewport — mobile (sidebar behind a menu) vs desktop (static sidebar + split view). */
  viewport: Viewport;
  /** The always-visible app shell (sidebar nav + signed-in user). */
  chrome: ChromeContext;
  /** Coarse mode of the foreground content surface. */
  mode: UIMode;
  /** Every content surface CURRENTLY VISIBLE to the user. Mobile shows one (chat OR cards);
   *  desktop shows several (chat + cards). This is "what the user is actually seeing". */
  surfaces: UIView[];
  /** The user currently focused on screen (expanded card / hands-free big card), or null. Sent to
   *  the agent so "rename this user" resolves to the right person across every mode. */
  selectedUser: SelectedUser | null;
  /** Browser timezone + coarse geolocation — attached to every agent interaction. */
  device: DeviceContext;
  /** Monotonic counter bumped on every state change — a cheap effect trigger for the bridge. */
  rev: number;
  /** Shared meeting list filter — kept in sync between the agent workspace and MeetingsPage. */
  meetingFilter: MeetingFilter;
  /** The chat conversation currently open (ChatPage), or null when chat isn't mounted/active.
   *  Lets the always-mounted UIContextBridge key its state-sync pushes to the right conversation
   *  from outside ChatPage's own component tree. */
  activeConversationId: string | null;
}

const initialState: UIContextState = {
  path: '',
  pageLabel: '',
  viewport: 'unknown',
  chrome: { nav: [], navCollapsed: false, user: { name: null, email: null } },
  mode: 'generic',
  surfaces: [{ kind: 'generic' }],
  selectedUser: null,
  device: {},
  rev: 0,
  meetingFilter: {},
  activeConversationId: null,
};

const uiContextSlice = createSlice({
  name: 'uiContext',
  initialState,
  reducers: {
    // Shallow-merge like brandSlice.setBrand: each writer owns a few top-level keys (the route
    // tracker sets path/label/mode/surfaces, the shell sets viewport/chrome, pages set mode/surfaces)
    // so they never clobber each other. `surfaces`/`chrome` are replaced wholesale, never deep-merged.
    setUIContext(state, action: PayloadAction<Partial<UIContextState>>) {
      return { ...state, ...action.payload, rev: state.rev + 1 };
    },
    // Merge (not replace) device fields — timezone is set on mount, geolocation arrives later.
    setDeviceContext(state, action: PayloadAction<Partial<DeviceContext>>) {
      state.device = { ...state.device, ...action.payload };
      state.rev += 1;
    },
    // The focused/hands-free user the agent should treat as the rename target (null clears it).
    setSelectedUser(state, action: PayloadAction<SelectedUser | null>) {
      state.selectedUser = action.payload;
      state.rev += 1;
    },
    setMeetingFilter(state, action: PayloadAction<MeetingFilter>) {
      state.meetingFilter = action.payload;
      state.rev += 1;
    },
    // ChatPage reports its conversation id here (and clears it to null on unmount) so the
    // always-mounted bridge can key its agent_realtime.state_sync pushes correctly.
    setActiveConversationId(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload;
      state.rev += 1;
    },
    clearUIContext() {
      return { ...initialState };
    },
  },
});

export const {
  setUIContext, setDeviceContext, setSelectedUser, setMeetingFilter, setActiveConversationId, clearUIContext,
} = uiContextSlice.actions;
export default uiContextSlice.reducer;
